/**
 * ResizeControl Component
 *
 * Reusable resize interface for fixtures, controls, and products.
 * Provides slider, live preview, batch operations, and default scale saving.
 *
 * @example
 * <ResizeControl
 *   entityType="fixture"
 *   entity={selectedFixture}
 *   allEntities={fixtures}
 *   onResize={handleResize}
 *   isUpdating={isUpdating}
 * />
 */

import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';

type EntityType = 'fixture' | 'control' | 'product';
type ResizeScope = 'single' | 'category' | 'subcategory' | 'type';

interface BaseEntity {
  id: string;
  scale?: number;
  category?: string | null;
  subcategory?: string | null;
  type?: string; // For controls
}

interface ResizeControlProps<T extends BaseEntity> {
  entityType: EntityType;
  entity: T;
  allEntities: T[];
  onResize: (
    ids: string[],
    scale: number,
    saveAsDefault: boolean,
    scope: ResizeScope,
  ) => Promise<void>;
  isUpdating?: boolean;
  onCancel?: () => void;
  /** Base scale multiplier for controls (from floor plan metadata). Default: 1.0 */
  baseScale?: number;
  /** Optional label override. Defaults to "Scale" */
  label?: string;
}

export function ResizeControl<T extends BaseEntity>({
  entityType,
  entity,
  allEntities,
  onResize,
  isUpdating = false,
  onCancel,
  baseScale = 1.0,
  label = 'Scale',
}: ResizeControlProps<T>) {
  // State
  const [resizeScope, setResizeScope] = useState<ResizeScope>('single');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [currentScale, setCurrentScale] = useState(1.0);
  const [hasUnsavedScale, setHasUnsavedScale] = useState(false);

  // Reset state when entity changes
  useEffect(() => {
    if (entity) {
      setCurrentScale(entity.scale || 1.0);
      setHasUnsavedScale(false);
      setResizeScope('single');
      setSaveAsDefault(false);
    }
  }, [entity.id]);

  // Cleanup effect: Revert visual changes on unmount if there are unsaved changes
  useEffect(() => {
    return () => {
      // Only revert if there were unsaved changes
      // We need to check the ref values since state won't be current in cleanup
      if (hasUnsavedScale && entity) {
        const originalScale = entity.scale || 1.0;

        // Revert visual scale via DOM (same logic as handleCancel but without state updates)
        if (entityType === 'control') {
          // For controls, target the icon-container inside (same as products)
          const controlElement = document.querySelector(
            `[data-control-id="${entity.id}"]`,
          ) as HTMLElement;
          const iconContainer = controlElement?.querySelector(
            '[data-icon-container]',
          ) as HTMLElement;

          if (iconContainer) {
            const currentTransform = iconContainer.style.transform;
            const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
            const currentRotation = rotationMatch ? rotationMatch[1] : '0';
            iconContainer.style.transition = 'none';
            // Apply baseScale multiplier to match how ControlLayer renders controls
            iconContainer.style.transform = `scale(${originalScale * baseScale}) rotate(${currentRotation}deg)`;

            // Restore the selection outline (remove inline overrides so React inline styles take effect)
            if (controlElement) {
              controlElement.style.boxShadow = '';
              controlElement.style.outline = '';
              controlElement.style.outlineOffset = '';
            }
          }
        } else {
          // For fixtures and products, try icon container first, then fallback to wrapper
          const iconContainer = document.querySelector(
            `[data-${entityType}-id="${entity.id}"] [data-icon-container]`,
          ) as HTMLElement;

          if (!iconContainer && entityType === 'fixture') {
            const fixtureWrapper = document.querySelector(
              `[data-${entityType}-id="${entity.id}"]`,
            ) as HTMLElement;

            if (fixtureWrapper) {
              // Check if this is a strip (contains SVG)
              const svgElement = fixtureWrapper.querySelector('svg');

              if (svgElement) {
                // For strips: Revert stroke-width on paths
                const paths = svgElement.querySelectorAll('path');
                const baseStrokeWidth = 4; // matches StripLightingMarker baseStrokeWidth

                paths.forEach(path => {
                  const revertedStroke = baseStrokeWidth * originalScale;
                  (path as SVGPathElement).style.strokeWidth = `${revertedStroke}px`;
                });
                // DO NOT modify wrapper transform for strips
              } else {
                // For regular marker fixtures, revert scale on the wrapper itself
                const currentTransform = fixtureWrapper.style.transform;
                const translateMatch = currentTransform.match(/translate\(([^)]+)\)/);
                const translate = translateMatch ? translateMatch[0] : 'translate(-50%, -50%)';
                fixtureWrapper.style.transition = 'none';
                // Apply baseScale multiplier to match how FixtureLayer renders fixtures
                fixtureWrapper.style.transform = `${translate} scale(${originalScale * baseScale})`;
              }
            }
          } else if (iconContainer) {
            const currentTransform = iconContainer.style.transform;
            const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
            const currentRotation = rotationMatch ? rotationMatch[1] : '0';
            iconContainer.style.transition = 'none';
            // Apply baseScale multiplier to match how rendering layers apply scale
            iconContainer.style.transform = `scale(${originalScale * baseScale}) rotate(${currentRotation}deg)`;
          }
        }

        // Also reset rotation handle and label if they exist
        // Use visual scale (originalScale * baseScale) for proper positioning
        const visualScale = originalScale * baseScale;

        const rotationHandle = document.querySelector(
          `[data-${entityType}-rotation-handle="${entity.id}"]`,
        ) as HTMLElement;

        if (rotationHandle) {
          const iconRadius = 24;
          const margin = 11;
          const calculatedOffset = iconRadius * visualScale + margin;
          const rotationOffset = Math.max(calculatedOffset, 35);
          rotationHandle.style.top = `-${rotationOffset}px`;
        }

        const labelElement = document.querySelector(
          `[data-${entityType}-label="${entity.id}"]`,
        ) as HTMLElement;

        if (labelElement) {
          const labelMarginTop = 6 + (visualScale - 1.0) * 10;
          labelElement.style.marginTop = `${labelMarginTop}px`;
        }

        // Badge positioning is now handled automatically - badge is inside scaled container
      }
    };
  }, [hasUnsavedScale, entity, entityType, baseScale]);

  // Calculate counts for each scope
  const getCounts = () => {
    if (entityType === 'control') {
      // For controls, use 'type' field
      const singleCount = 1;
      const typeCount = allEntities.filter(e => e.type === entity.type).length;
      return { singleCount, categoryCount: typeCount, subcategoryCount: 0 };
    } else {
      // For fixtures and products, use category/subcategory
      const singleCount = 1;
      const categoryCount = allEntities.filter(e => e.category === entity.category).length;
      const subcategoryCount = entity.subcategory
        ? allEntities.filter(
            e => e.category === entity.category && e.subcategory === entity.subcategory,
          ).length
        : 0;
      return { singleCount, categoryCount, subcategoryCount };
    }
  };

  const { singleCount, categoryCount, subcategoryCount } = getCounts();

  // Handle save
  const handleSave = async () => {
    const entitiesToResize = allEntities.filter(e => {
      if (resizeScope === 'single') {
        return e.id === entity.id;
      } else if (resizeScope === 'type') {
        // For controls
        return e.type === entity.type;
      } else if (resizeScope === 'category') {
        return e.category === entity.category;
      } else {
        // subcategory
        return e.category === entity.category && e.subcategory === entity.subcategory;
      }
    });

    const ids = entitiesToResize.map(e => e.id);
    await onResize(ids, currentScale, saveAsDefault, resizeScope);
    setHasUnsavedScale(false);
  };

  // Handle cancel
  const handleCancel = () => {
    const originalScale = entity.scale || 1.0;
    setCurrentScale(originalScale);
    setHasUnsavedScale(false);

    // Revert visual scale via DOM
    if (entityType === 'control') {
      // For controls, target the icon-container inside (same as products)
      const controlElement = document.querySelector(
        `[data-control-id="${entity.id}"]`,
      ) as HTMLElement;
      const iconContainer = controlElement?.querySelector('[data-icon-container]') as HTMLElement;

      if (iconContainer) {
        const currentTransform = iconContainer.style.transform;
        const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
        const currentRotation = rotationMatch ? rotationMatch[1] : '0';
        iconContainer.style.transition = 'none';
        // Apply baseScale multiplier to match how ControlLayer renders controls
        iconContainer.style.transform = `scale(${originalScale * baseScale}) rotate(${currentRotation}deg)`;

        // Restore the selection outline (remove inline overrides so React inline styles take effect)
        if (controlElement) {
          controlElement.style.boxShadow = '';
          controlElement.style.outline = '';
          controlElement.style.outlineOffset = '';
        }
      }
    } else {
      // For fixtures and products, try icon container first, then fallback to wrapper
      const iconContainer = document.querySelector(
        `[data-${entityType}-id="${entity.id}"] [data-icon-container]`,
      ) as HTMLElement;

      // If no icon container found (marker rendering), target the wrapper element directly
      if (!iconContainer && entityType === 'fixture') {
        const fixtureWrapper = document.querySelector(
          `[data-${entityType}-id="${entity.id}"]`,
        ) as HTMLElement;

        if (fixtureWrapper) {
          // Check if this is a strip (contains SVG)
          const svgElement = fixtureWrapper.querySelector('svg');

          if (svgElement) {
            // For strips: Revert stroke-width on paths
            const paths = svgElement.querySelectorAll('path');
            const baseStrokeWidth = 4; // matches StripLightingMarker baseStrokeWidth

            paths.forEach(path => {
              const revertedStroke = baseStrokeWidth * originalScale;
              (path as SVGPathElement).style.strokeWidth = `${revertedStroke}px`;
            });
            // DO NOT modify wrapper transform for strips
          } else {
            // For regular marker fixtures, revert scale on the wrapper itself
            const currentTransform = fixtureWrapper.style.transform;
            const translateMatch = currentTransform.match(/translate\(([^)]+)\)/);
            const translate = translateMatch ? translateMatch[0] : 'translate(-50%, -50%)';
            fixtureWrapper.style.transition = 'none';
            // Apply baseScale multiplier to match how FixtureLayer renders fixtures
            fixtureWrapper.style.transform = `${translate} scale(${originalScale * baseScale})`;
          }
        }
      } else if (iconContainer) {
        // Icon rendering path - update the icon container transform only
        const currentTransform = iconContainer.style.transform;
        const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
        const currentRotation = rotationMatch ? rotationMatch[1] : '0';
        iconContainer.style.transition = 'none';
        // Apply baseScale multiplier to match how rendering layers apply scale
        iconContainer.style.transform = `scale(${originalScale * baseScale}) rotate(${currentRotation}deg)`;
      }
    }

    // Update rotation handle and label position using visual scale
    const visualScale = originalScale * baseScale;

    const rotationHandle = document.querySelector(
      `[data-${entityType}-rotation-handle="${entity.id}"]`,
    ) as HTMLElement;

    if (rotationHandle) {
      const iconRadius = 24;
      const margin = 11;
      const calculatedOffset = iconRadius * visualScale + margin;
      const rotationOffset = Math.max(calculatedOffset, 35);
      rotationHandle.style.top = `-${rotationOffset}px`;
    }

    const labelElement = document.querySelector(
      `[data-${entityType}-label="${entity.id}"]`,
    ) as HTMLElement;

    if (labelElement) {
      const labelMarginTop = 6 + (visualScale - 1.0) * 10;
      labelElement.style.marginTop = `${labelMarginTop}px`;
    }

    // Badge positioning is now handled automatically - badge is inside scaled container

    onCancel?.();
  };

  // Handle slider change
  const handleSliderChange = (value: number) => {
    setCurrentScale(value);
    setHasUnsavedScale(true);

    // Update visual scale via DOM for live preview
    if (entityType === 'control') {
      // For controls, target the icon-container inside (same as products)
      // The parent [data-control-id] has fixed dimensions, so we must scale the inner container
      const controlElement = document.querySelector(
        `[data-control-id="${entity.id}"]`,
      ) as HTMLElement;
      const iconContainer = controlElement?.querySelector('[data-icon-container]') as HTMLElement;

      if (iconContainer) {
        const currentTransform = iconContainer.style.transform;
        const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
        const currentRotation = rotationMatch ? rotationMatch[1] : '0';
        iconContainer.style.transition = 'transform 0.1s ease-out';
        // Apply baseScale multiplier to match how ControlLayer renders controls
        iconContainer.style.transform = `scale(${value * baseScale}) rotate(${currentRotation}deg)`;

        // Update the selection outline offset to stay outside the scaled icon
        // The parent element has fixed dimensions (markerSizePixels x markerSizePixels)
        if (controlElement) {
          const markerSize = controlElement.offsetWidth;
          const effectiveScale = value * baseScale;
          const outlineOffset = ((effectiveScale - 1) * markerSize) / 2;
          controlElement.style.outlineOffset = `${outlineOffset}px`;
        }
      }
    } else {
      // For fixtures and products, try icon container first, then fallback to wrapper
      const iconContainer = document.querySelector(
        `[data-${entityType}-id="${entity.id}"] [data-icon-container]`,
      ) as HTMLElement;

      // If no icon container found (marker rendering), target the wrapper element directly
      if (!iconContainer && entityType === 'fixture') {
        const fixtureWrapper = document.querySelector(
          `[data-${entityType}-id="${entity.id}"]`,
        ) as HTMLElement;

        if (fixtureWrapper) {
          // Check if this is a strip (contains SVG)
          const svgElement = fixtureWrapper.querySelector('svg');

          if (svgElement) {
            // For strips: Update stroke-width on paths, NOT wrapper transform
            // This keeps the strip position fixed while only changing line thickness
            const paths = svgElement.querySelectorAll('path');
            const baseStrokeWidth = 4; // matches StripLightingMarker baseStrokeWidth

            paths.forEach(path => {
              const newStroke = baseStrokeWidth * value;
              (path as SVGPathElement).style.strokeWidth = `${newStroke}px`;
            });
            // DO NOT modify wrapper transform for strips
          } else {
            // For regular marker fixtures, scale the wrapper itself
            const currentTransform = fixtureWrapper.style.transform;
            const translateMatch = currentTransform.match(/translate\(([^)]+)\)/);
            const translate = translateMatch ? translateMatch[0] : 'translate(-50%, -50%)';
            fixtureWrapper.style.transition = 'transform 0.1s ease-out';
            // Apply baseScale multiplier to match how FixtureLayer renders fixtures
            fixtureWrapper.style.transform = `${translate} scale(${value * baseScale})`;
          }
        }
      } else if (iconContainer) {
        // Icon rendering path - ONLY update transform (like LV Products)
        // Note: Container size will update on save/re-render
        const currentTransform = iconContainer.style.transform;
        const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
        const currentRotation = rotationMatch ? rotationMatch[1] : '0';
        iconContainer.style.transition = 'transform 0.1s ease-out';
        // Apply baseScale multiplier to match how rendering layers apply scale
        iconContainer.style.transform = `scale(${value * baseScale}) rotate(${currentRotation}deg)`;
      }
    }

    // Update rotation handle and label position using visual scale
    const visualScale = value * baseScale;

    // Update rotation handle position if it exists
    const rotationHandle = document.querySelector(
      `[data-${entityType}-rotation-handle="${entity.id}"]`,
    ) as HTMLElement;

    if (rotationHandle) {
      const iconRadius = 24;
      const margin = 11;
      const calculatedOffset = iconRadius * visualScale + margin;
      const rotationOffset = Math.max(calculatedOffset, 35);
      rotationHandle.style.top = `-${rotationOffset}px`;
    }

    // Update label position if it exists
    const labelElement = document.querySelector(
      `[data-${entityType}-label="${entity.id}"]`,
    ) as HTMLElement;

    if (labelElement) {
      const labelMarginTop = 6 + (visualScale - 1.0) * 10;
      labelElement.style.marginTop = `${labelMarginTop}px`;
    }

    // Badge positioning is now handled automatically - badge is inside scaled container
  };

  // Get display name for category/type
  const getCategoryDisplayName = () => {
    if (entityType === 'control' && entity.type) {
      return entity.type.replace(/_/g, ' ').replace(/-/g, ' ');
    }
    return entity.category?.replace(/_/g, ' ').replace(/-/g, ' ') || 'category';
  };

  const getSubcategoryDisplayName = () => {
    return entity.subcategory?.replace(/_/g, ' ').replace(/-/g, ' ') || 'subcategory';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-sm font-medium">{Math.round(currentScale * 100)}%</span>
      </div>

      <Slider
        min={0.2}
        max={5.0}
        step={0.05}
        value={[currentScale]}
        onValueChange={([value]) => handleSliderChange(value)}
        className="w-full"
      />

      {/* Scale range labels */}
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>20%</span>
        <span>100%</span>
        <span>500%</span>
      </div>

      {/* Apply to section - only show when there are unsaved changes */}
      {hasUnsavedScale && (
        <div className="space-y-3 pt-2">
          <Label className="text-sm">Apply to:</Label>

          <RadioGroup
            value={resizeScope}
            onValueChange={(value: ResizeScope) => setResizeScope(value)}
          >
            {/* Single entity */}
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="single" id="resize-single" />
              <label
                htmlFor="resize-single"
                className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                This {entityType} only ({singleCount} item)
              </label>
            </div>

            {/* Category or Type */}
            {entityType === 'control' ? (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="type" id="resize-type" />
                <label
                  htmlFor="resize-type"
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  All {getCategoryDisplayName()} controls ({categoryCount} items)
                </label>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="category" id="resize-category" />
                <label
                  htmlFor="resize-category"
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  All {getCategoryDisplayName()} {entityType}s ({categoryCount} items)
                </label>
              </div>
            )}

            {/* Subcategory - only for fixtures/products */}
            {entityType !== 'control' && entity.subcategory && subcategoryCount > 0 && (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="subcategory" id="resize-subcategory" />
                <label
                  htmlFor="resize-subcategory"
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  All {getCategoryDisplayName()} &gt; {getSubcategoryDisplayName()} {entityType}s (
                  {subcategoryCount} items)
                </label>
              </div>
            )}
          </RadioGroup>

          {/* Save as default checkbox */}
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="save-default"
              checked={saveAsDefault}
              onCheckedChange={checked => setSaveAsDefault(checked as boolean)}
            />
            <label
              htmlFor="save-default"
              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Save as default size
            </label>
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isUpdating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isUpdating} className="flex-1">
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
